import "./shipment-details.css";

import { memo } from "react";
import { useParams } from "react-router";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import { AdditionalDetails } from "../additional-details/additional-details.tsx";
import { CarrierCard } from "../carrier-card/carrier-card.tsx";
import { CarrierService } from "../carrier-service/carrier-service.tsx";
import { CustomerCard } from "../customer-card/customer-card.tsx";
import { FreightDetailsV2 } from "../freight-details/freight-details.v2.tsx";
import { InsuranceCard } from "../insurance-card/insurance-card.tsx";
import { SpecialInstructions } from "../special-instructions/special-instructions.tsx";
import { TqlSalesContacts } from "../tql-sales-contacts/tql-sales-contacts.tsx";
import { VendorCard } from "../vendor-card/vendor-card.tsx";
import { ShipmentLocationCard } from "./shipment-location-card.tsx";

export const ShipmentDetails = memo(
	({
		onRerate,
		shipment,
	}: {
		onRerate?: () => void;
		shipment: AugmentedShipment;
	}) => {
		const { poNumber = "" } = useParams();
		const numericPoNumber = Number(poNumber);
		const { isInternal } = useDeploymentType();
		const { customer, carrier, carrierService, insurance, payVendor } =
			shipment;
		const pickup = shipment.shipmentDetails?.pickup;
		const delivery = shipment.shipmentDetails?.delivery;
		const freightDetails = shipment.shipmentDetails?.freightDetails;
		const additionalDetails = shipment.shipmentDetails?.additionalDetails;

		return (
			<>
				<div className={"shipment-details-group"}>
					<div className={"side-by-side-cards"}>
						<ShipmentLocationCard
							customerId={customer?.id ?? 0}
							details={pickup}
							name={"pickup"}
						/>
						<ShipmentLocationCard
							customerId={customer?.id ?? 0}
							details={delivery}
							name={"delivery"}
						/>
					</div>
					<div className={"special-instructions-card"}>
						<SpecialInstructions
							bolNotations={additionalDetails?.bolNotations ?? undefined}
							deliveryInstructions={delivery?.specialInstructions ?? undefined}
							pickupInstructions={pickup?.specialInstructions ?? undefined}
						/>
					</div>
					<div className={"freight-details-card"}>
						<FreightDetailsV2
							accessorials={shipment.accessorials ?? undefined}
							details={freightDetails}
						/>
					</div>
					<div className={"additional-details-card"}>
						<AdditionalDetails details={additionalDetails} />
					</div>
				</div>

				<CarrierService
					details={carrierService}
					shipmentDate={pickup?.scheduled ?? ""}
				/>

				<div
					className={`${isInternal ? "side-by-side-cards" : "card-wrapper"}`}
				>
					<InsuranceCard
						details={insurance}
						insuredAmount={insurance?.totalAmount ?? 0}
						isInsured={insurance?.isInsured ?? false}
						loadValue={additionalDetails?.loadValue}
						onRerate={onRerate}
						poNumber={numericPoNumber}
					/>
					{isInternal ? (
						<VendorCard details={payVendor} poNumber={poNumber} />
					) : null}
				</div>

				<div className={"side-by-side-cards"}>
					<CustomerCard details={customer} />
					<CarrierCard
						additionalDetails={additionalDetails}
						details={carrier}
					/>
				</div>

				<div className={"card-wrapper"}>
					<TqlSalesContacts details={shipment.tqlSalesContacts} />
				</div>
			</>
		);
	},
);

ShipmentDetails.displayName = "ShipmentDetails";
